/* App state singleton + undo history. */
import type { Constraint, Doc, ItemKind, Pt, Wall } from './types.ts';

export const $ = (s: string) => document.querySelector(s) as HTMLElement;

export const emptyDoc = (): Doc => ({ version: 1, walls: [], openings: [], fixtures: [], labels: [], constraints: [] });

export interface SnapInd { type: 'end' | 'edge' | 'mid'; x: number; y: number; wall?: import('./types.ts').Wall }
export type Guide =
  | { kind: 'line'; x1: number; y1: number; x2: number; y2: number }
  | { kind: 'perp'; x: number; y: number; a1: number; a2: number }
  // the dragged/drawn wall is ⊥/∥ to `wall` — highlight it + badge at bx,by
  | { kind: 'ref'; wall: Wall; rel: '⊥' | '∥'; bx: number; by: number }
  // a fixture is magnetised flush to this wall
  | { kind: 'face'; wall: Wall };

export const state = {
  doc: emptyDoc(),
  name: null as string | null,
  view: { x: -40, y: -30, zoom: 4 },   // x,y = world inches at canvas top-left
  tool: 'select',
  fixtureType: 'toilet',
  doorType: 'door',            // last-used door-family type; the rail's Door button restores it
  shareView: null as null | { id: string; uid: string; name: string },  // viewing someone's share link
  collab: null as null | { owner: string; layouts: string[] },           // editing someone else's layout(s) by invitation
  palQuery: '',                // fixture palette search text (survives re-renders)
  sel: new Set<string>(),
  hover: null as string | null,
  snapStep: 1,
  // view toggles (bottom-bar View popup, persisted); hidden things aren't clickable
  show: { dims: true, badges: true, labels: true, areas: true, below: true, ...((): object => {
    try { return JSON.parse(localStorage.getItem('playout:view') || '{}'); } catch { return {}; }
  })() } as { dims: boolean; badges: boolean; labels: boolean; areas: boolean; below: boolean },
  /* collaborators' cursors on this layout (cloud mode), by uid */
  presence: new Map<string, { x: number; y: number; name: string; color: string; ts: number }>(),
  /* the floor under this one (multi-floor projects), drawn faintly underneath for alignment */
  underlay: null as null | { name: string; doc: Doc },
  rooms: [] as import('./rooms.ts').Room[],   // detected enclosed rooms (refreshed by the renderer when walls change)
  drag: null as any,          // active drag op
  /* startHost: wall the draft's first point edge/midpoint-snapped onto (T-junction) */
  wallDraft: null as { ax: number; ay: number; typed: string; startHost?: { id: string; mid?: boolean } } | null,
  roomDraft: null as { ax: number; ay: number; bx: number; by: number } | null,   // Room tool drag (centerline corners)
  /* Constrain tool: armed relationship + the first wall picked (with click pt,
     used to choose which end for coin/mid) */
  constrainType: 'perp',
  constrainFirst: null as { id: string; x: number; y: number } | null,
  placePreview: null as Pt | null,  // world pt for fixture/door/window preview
  placeGhost: null as { w: number; h: number; rot: number; x?: number; y?: number } | null,
  measure: null as { ax: number; ay: number; bx: number; by: number } | null,
  dirty: false,
  lastMtime: 0,
  spaceDown: false,
  snapInd: null as SnapInd | null,  // live join indicator
  guides: [] as Guide[],            // smart-guide overlays
  lastMouse: { x: 0, y: 0 } as Pt,  // last pointer position in world inches
};

export const keys = { alt: false, shift: false };

/* Cross-cutting callbacks, assigned once in main.ts. Modules low in the
   dependency graph (this one, edit, ops) call UI refreshes through here so the
   module graph stays acyclic. */
export const hooks = {
  markDirty: () => {},
  markDirtySoft: () => {},
  renderPanel: () => {},
  requestDraw: () => {},
  saveNow: () => {},
  setTool: (_id: string) => {},
  coach: (_step: string) => {},      // one-time tips at first-use moments (coach.ts)
  notify: (_msg: string) => {},   // in-app notice modal (never window.alert)
  confirm: (_msg: string, _ok?: string) => Promise.resolve(false),   // in-app confirm modal
};

const undoStack: string[] = [], redoStack: string[] = [];
export function pushUndo(): void {
  undoStack.push(JSON.stringify(state.doc));
  if (undoStack.length > 120) undoStack.shift();
  redoStack.length = 0;
}
export function undo(): void {
  if (!undoStack.length) return;
  redoStack.push(JSON.stringify(state.doc));
  state.doc = JSON.parse(undoStack.pop()!);
  afterDocSwap();
}
export function redo(): void {
  if (!redoStack.length) return;
  undoStack.push(JSON.stringify(state.doc));
  state.doc = JSON.parse(redoStack.pop()!);
  afterDocSwap();
}
export function clearHistory(): void { undoStack.length = 0; redoStack.length = 0; }
function afterDocSwap(): void {
  const ids = allIds();
  state.sel = new Set([...state.sel].filter(id => ids.has(id)));
  hooks.markDirty(); hooks.renderPanel();
}

export function allIds(): Set<string> {
  const s = new Set<string>();
  for (const k of ['walls', 'openings', 'fixtures', 'labels'] as ItemKind[])
    for (const it of state.doc[k]) s.add(it.id);
  return s;
}

export function findItem(id: string): { kind: ItemKind; item: any } | null {
  for (const k of ['walls', 'openings', 'fixtures', 'labels'] as ItemKind[]) {
    const it = state.doc[k].find((i: any) => i.id === id);
    if (it) return { kind: k, item: it };
  }
  return null;
}

export const constraintsOf = (wallId: string): Constraint[] =>
  (state.doc.constraints || []).filter(c => c.a === wallId || ('b' in c && c.b === wallId));
